import {
  formatAccountLabel,
  KEPT_ECRITURES_RULE,
  PRESTATION_KIND_RULES,
  type PrestationKind,
} from '@cpce/shared';
import { Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLeafAccountOptions } from '@/api/financial-accounts/financial-accounts.queries';
import { useChangePrestationAccount } from '@/api/prestations/prestations.mutations';
import { usePrestationAccount } from '@/api/prestations/prestations.queries';
import { Button } from '@/components/Button';
import { Combobox } from '@/components/Combobox';
import { ConfirmModal } from '@/components/ConfirmModal';
import { FieldLabel } from '@/components/form/FieldLabel';
import { KeptEcrituresIndicator } from '@/components/KeptEcrituresIndicator';

export function PrestationAccountField({ prestationId }: { prestationId: number }) {
  const { data: state } = usePrestationAccount(prestationId);
  const [open, setOpen] = useState(false);

  if (!state) return '-';

  return (
    <div className="flex items-center gap-2">
      <span>{state.account ? formatAccountLabel(state.account) : 'Défini au premier dossier'}</span>
      <KeptEcrituresIndicator
        entries={state.entriesOnOtherAccounts}
        title="Écritures restées sur un autre compte"
      />
      <Button
        type="button"
        icon={Pencil}
        onClick={() => setOpen(true)}
        className="text-gray-600"
        title="Changer le compte"
      />
      {open && (
        <ChangePrestationAccountModal
          prestationId={prestationId}
          kind={state.kind}
          currentAccountId={state.account?.id ?? null}
          closeModal={() => setOpen(false)}
        />
      )}
    </div>
  );
}

interface ChangePrestationAccountModalProps {
  prestationId: number;
  kind: PrestationKind;
  currentAccountId: number | null;
  closeModal: () => void;
}

function ChangePrestationAccountModal(props: ChangePrestationAccountModalProps) {
  const { prestationId, kind, currentAccountId, closeModal } = props;
  const { accountClass, accountLabel } = PRESTATION_KIND_RULES[kind];
  const { data: accounts = [], isLoading } = useLeafAccountOptions(accountClass);
  const changeAccount = useChangePrestationAccount(prestationId);
  const [accountId, setAccountId] = useState<number | undefined>(currentAccountId ?? undefined);

  const options = useMemo(
    () => accounts.map((account) => ({ value: account.id, label: formatAccountLabel(account) })),
    [accounts]
  );

  return (
    <ConfirmModal
      title={`Changer le compte ${accountLabel}`}
      info={{
        title: 'Écritures des dossiers',
        text: `Les écritures des dossiers de cette prestation passent sur le nouveau compte. ${KEPT_ECRITURES_RULE}`,
      }}
      disabled={accountId === undefined}
      onConfirm={async () => {
        if (accountId === undefined) return;
        return changeAccount.mutate({ financialAccountId: accountId });
      }}
      closeModal={closeModal}
    >
      <FieldLabel label={`Compte ${accountLabel}`}>
        <Combobox
          options={options}
          value={accountId}
          onChange={setAccountId}
          isLoading={isLoading}
        />
      </FieldLabel>
    </ConfirmModal>
  );
}
